import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { 
  Container, Typography, Box, Paper, Grid, TextField, Button, 
  Divider, Switch, FormControlLabel, Alert, CircularProgress,
  Card, CardContent, Avatar, Chip
} from '@mui/material';
import { useMutation, gql, useQuery } from '@apollo/client';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import AuthContext from '../contexts/AuthContext';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WorkIcon from '@mui/icons-material/Work';
import PhoneNumberField from '../components/PhoneNumberField';
import { isValidPhoneNumber } from 'react-phone-number-input';

// GraphQL queries et mutations
const GET_USER = gql`
  query GetUser($id: ID!) {
    user(id: $id) {
      id
      firstName
      lastName
      email
      phoneNumber
      role
      department {
        id
        name
      }
      otpEnabled
      hostAvailabilities {
        id
        dayOfWeek
        startTime
        endTime
        isActive
      }
    }
    hostAvailabilities {
      id
      dayOfWeek
      startTime
      endTime
      isActive
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser(
    $id: ID!
    $firstName: String
    $lastName: String
    $phoneNumber: BigInt
  ) {
    updateUser(
      id: $id
      firstName: $firstName
      lastName: $lastName
      phoneNumber: $phoneNumber
    ) {
      success
      message
      user {
        id
        firstName
        lastName
        email
        phoneNumber
        role
        department {
          id
          name
        }
        otpEnabled
      }
    }
  }
`;

const TOGGLE_OTP = gql`
  mutation ToggleOTP($enabled: Boolean!) {
    toggleOtp(enabled: $enabled) {
      success
      message
      user {
        id
        otpEnabled
      }
    }
  }
`;

const CHANGE_PASSWORD = gql`
  mutation ChangePassword(
    $oldPassword: String!
    $newPassword: String!
  ) {
    changePassword(
      oldPassword: $oldPassword
      newPassword: $newPassword
    ) {
      success
      message
    }
  }
`;

const SET_MY_HOST_AVAILABILITIES = gql`
  mutation SetMyHostAvailabilities($availabilities: [HostAvailabilityInput]!) {
    setMyHostAvailabilities(availabilities: $availabilities) {
      success
      message
      availabilities {
        id
        dayOfWeek
        startTime
        endTime
        isActive
      }
    }
  }
`;

// Schémas de validation
const profileSchema = Yup.object().shape({
  firstName: Yup.string().required('Le prénom est requis'),
  lastName: Yup.string().required('Le nom est requis'),
  phoneNumber: Yup.string()
    .nullable()
    .test('is-valid-phone', 'Numéro de téléphone invalide', (value) => {
      if (!value) return true;
      return isValidPhoneNumber(value);
    })
});

const passwordSchema = Yup.object().shape({
  oldPassword: Yup.string().required('Le mot de passe actuel est requis'),
  newPassword: Yup.string()
    .required('Le nouveau mot de passe est requis')
    .min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Les mots de passe doivent correspondre')
    .required('La confirmation du mot de passe est requise')
});

function Profile() {
  const normalizeDayOfWeek = useCallback((value) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      if (/^\d+$/.test(value)) return Number(value);
      const match = value.match(/(\d+)$/);
      if (match) return Number(match[1]);
    }
    return null;
  }, []);

  const { user, loading: authLoading, refreshUserData } = useContext(AuthContext);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [availabilityValidationError, setAvailabilityValidationError] = useState('');
  const [weeklyAvailability, setWeeklyAvailability] = useState(
    Array.from({ length: 7 }).map((_, day) => ({
      dayOfWeek: day,
      isActive: false,
      startTime: '09:00',
      endTime: '17:00',
    }))
  );
  const dayLabels = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  const availabilityHydratedRef = useRef(false);
  const lastSavedAvailabilityRef = useRef('');
  const availabilitySaveTimeoutRef = useRef(null);

  const toMinutes = useCallback((timeValue) => {
    const value = String(timeValue || '').slice(0, 5);
    const [hour, minute] = value.split(':').map(Number);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return (hour * 60) + minute;
  }, []);

  const hasInvalidTimeRange = useCallback((slot) => {
    if (!slot?.isActive) return false;
    const start = toMinutes(slot.startTime);
    const end = toMinutes(slot.endTime);
    if (start === null || end === null) return true;
    return end <= start;
  }, [toMinutes]);
  
  // Récupérer les données fraîches de l'utilisateur
  const { data: userData, loading: userLoading, refetch } = useQuery(GET_USER, {
    variables: { id: user?.id },
    skip: !user?.id,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      console.log("Données utilisateur récupérées:", data);
    },
    onError: (error) => {
      console.error("Erreur lors de la récupération des données utilisateur:", error);
      setErrorMessage(`Erreur lors de la récupération des données: ${error.message}`);
    }
  });
  
  // Utiliser les données fraîches ou les données du contexte
  const currentUser = userData?.user || user;
  
  console.log("Utilisateur du contexte:", user);
  console.log("Utilisateur courant:", currentUser);
  
  // Valeurs initiales du formulaire
  const initialValues = {
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    phoneNumber: currentUser?.phoneNumber ? `+${currentUser.phoneNumber}` : ''
  };
  
  // Mutation pour mettre à jour le profil
  const [updateUser, { loading: updateLoading }] = useMutation(UPDATE_USER, {
    onCompleted: (data) => {
      if (data.updateUser.success) {
        setSuccessMessage(data.updateUser.message || 'Profil mis à jour avec succès');
        setErrorMessage('');
        
        // Rafraîchir les données locales
        refetch();
        
        // Forcer un rechargement de la page pour mettre à jour toutes les données
        // Cette approche est plus brutale mais garantit que toutes les données sont à jour
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setErrorMessage(data.updateUser.message || 'Erreur lors de la mise à jour du profil');
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      setErrorMessage(`Erreur: ${error.message}`);
      setSuccessMessage('');
    }
  });
  
  // Mutation pour activer/désactiver l'authentification à deux facteurs
  const [toggleOtp, { loading: otpLoading }] = useMutation(TOGGLE_OTP, {
    onCompleted: (data) => {
      if (data.toggleOtp.success) {
        setSuccessMessage(data.toggleOtp.message);
        setErrorMessage('');
      } else {
        setErrorMessage(data.toggleOtp.message || 'Erreur lors de la modification des paramètres 2FA');
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      setErrorMessage(`Erreur: ${error.message}`);
      setSuccessMessage('');
    }
  });
  
  // Mutation pour changer le mot de passe
  const [changePassword, { loading: passwordLoading }] = useMutation(CHANGE_PASSWORD, {
    onCompleted: (data) => {
      if (data.changePassword.success) {
        setSuccessMessage(data.changePassword.message || 'Mot de passe changé avec succès');
        setErrorMessage('');
      } else {
        setErrorMessage(data.changePassword.message || 'Erreur lors du changement de mot de passe');
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      setErrorMessage(`Erreur: ${error.message}`);
      setSuccessMessage('');
    }
  });

  const [setMyHostAvailabilities, { loading: availabilityLoading }] = useMutation(SET_MY_HOST_AVAILABILITIES, {
    onCompleted: (data) => {
      if (data.setMyHostAvailabilities.success) {
        setErrorMessage('');
        refetch();
      } else {
        setErrorMessage(data.setMyHostAvailabilities.message || "Erreur lors de l'enregistrement des disponibilités");
      }
    },
    onError: (error) => {
      setErrorMessage(`Erreur: ${error.message}`);
    }
  });
  
  // Gérer la mise à jour du profil
  const handleUpdateProfile = (values) => {
    const phoneNumberValue = values.phoneNumber
      ? parseInt(String(values.phoneNumber).replace(/\D/g, ''), 10)
      : null;

    updateUser({
      variables: {
        id: currentUser.id,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: phoneNumberValue
      }
    });
  };
  
  // Gérer le changement de mot de passe
  const handleChangePassword = (values, { resetForm }) => {
    changePassword({
      variables: {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword
      }
    }).then((response) => {
      if (response.data.changePassword.success) {
        resetForm();
      }
    });
  };
  
  // Gérer l'activation/désactivation de l'authentification à deux facteurs
  const handleToggleOtp = (event) => {
    toggleOtp({
      variables: {
        enabled: event.target.checked
      }
    });
  };

  // Effacer les messages après un certain temps
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  useEffect(() => {
    if (currentUser?.role !== 'HOST') return;

    const availabilitySource = userData?.hostAvailabilities?.length
      ? userData.hostAvailabilities
      : (currentUser?.hostAvailabilities || []);

    const byDay = new Map();
    availabilitySource.forEach((slot) => {
      const day = normalizeDayOfWeek(slot.dayOfWeek);
      if (day === null || day < 0 || day > 6) return;
      byDay.set(day, {
        dayOfWeek: day,
        isActive: slot.isActive,
        startTime: String(slot.startTime || '09:00').slice(0, 5),
        endTime: String(slot.endTime || '17:00').slice(0, 5),
      });
    });

    const normalizedWeeklyAvailability = Array.from({ length: 7 }).map((_, day) => (
        byDay.get(day) || {
          dayOfWeek: day,
          isActive: false,
          startTime: '09:00',
          endTime: '17:00',
        }
      ));

    const signature = JSON.stringify(normalizedWeeklyAvailability);
    lastSavedAvailabilityRef.current = signature;
    availabilityHydratedRef.current = true;
    setWeeklyAvailability(normalizedWeeklyAvailability);
  }, [currentUser, userData?.hostAvailabilities, normalizeDayOfWeek]);

  useEffect(() => {
    if (currentUser?.role !== 'HOST') return undefined;
    if (!availabilityHydratedRef.current) return undefined;

    const invalidSlots = weeklyAvailability.filter(hasInvalidTimeRange);
    if (invalidSlots.length > 0) {
      setAvailabilityValidationError("L'heure de fin doit être strictement supérieure à l'heure de début pour chaque jour actif.");
      if (availabilitySaveTimeoutRef.current) {
        clearTimeout(availabilitySaveTimeoutRef.current);
      }
      return undefined;
    }
    setAvailabilityValidationError('');

    const signature = JSON.stringify(weeklyAvailability);
    if (signature === lastSavedAvailabilityRef.current) return undefined;

    if (availabilitySaveTimeoutRef.current) {
      clearTimeout(availabilitySaveTimeoutRef.current);
    }

    availabilitySaveTimeoutRef.current = setTimeout(() => {
      setMyHostAvailabilities({
        variables: {
          availabilities: weeklyAvailability.map((slot) => ({
            dayOfWeek: slot.dayOfWeek,
            startTime: slot.startTime,
            endTime: slot.endTime,
            isActive: slot.isActive,
          })),
        },
      });
      lastSavedAvailabilityRef.current = signature;
    }, 500);

    return () => {
      if (availabilitySaveTimeoutRef.current) {
        clearTimeout(availabilitySaveTimeoutRef.current);
      }
    };
  }, [weeklyAvailability, currentUser?.role, setMyHostAvailabilities, hasInvalidTimeRange]);
  
  // Gérer les états de chargement
  const loading = authLoading || userLoading;
  
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  // Vérifier que l'utilisateur est défini
  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">
            Impossible de charger les informations utilisateur. Veuillez vous reconnecter.
          </Alert>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom component="h1" marginTop={2}>
        Profil Utilisateur
      </Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{ width: 100, height: 100, mx: 'auto', mb: 2 }}
                >
                  {currentUser.firstName?.charAt(0)}{currentUser.lastName?.charAt(0)}
                </Avatar>
                <Typography variant="h5" gutterBottom>
                  {currentUser.firstName} {currentUser.lastName}
                </Typography>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {currentUser.email}
                </Typography>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  <PhoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {currentUser.phoneNumber || 'Non spécifié'}
                </Typography>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  <WorkIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {currentUser.department?.name || 'Aucun département'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Chip 
                    label={
                      currentUser.role === 'ADMIN' ? 'Administrateur' :
                      currentUser.role === 'MANAGER' ? 'Gestionnaire' :
                      currentUser.role === 'ACCEUIL' ? 'Accueil' :
                      currentUser.role === 'HOST' ? 'Hôte' : 'Visiteur'
                    }
                    color={
                      currentUser.role === 'ADMIN' ? 'error' :
                      currentUser.role === 'MANAGER' ? 'warning' :
                      currentUser.role === 'ACCEUIL' ? 'warning' :
                      currentUser.role === 'HOST' ? 'info' : 'default'
                    }
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Informations Personnelles
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Formik
                initialValues={initialValues}
                validationSchema={profileSchema}
                onSubmit={handleUpdateProfile}
                enableReinitialize={true}
              >
                {({ errors, touched, handleChange, handleBlur, values, setFieldValue, setFieldTouched }) => (
                  <Form>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="firstName"
                          label="Prénom"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.firstName && Boolean(errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="lastName"
                          label="Nom"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.lastName && Boolean(errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <PhoneNumberField
                          name="phoneNumber"
                          label="Numéro de téléphone"
                          value={values.phoneNumber}
                          onChange={(value) => setFieldValue('phoneNumber', value || '')}
                          onBlur={() => setFieldTouched('phoneNumber', true)}
                          error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                          helperText={touched.phoneNumber && errors.phoneNumber}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={updateLoading}
                        >
                          {updateLoading ? <CircularProgress size={24} /> : 'Mettre à jour le profil'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                )}
              </Formik>
            </Paper>
            {currentUser.role === 'HOST' && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WorkIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Disponibilités Hebdomadaires
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Définissez vos créneaux par jour. Ces créneaux filtrent les dates et heures proposées lors de la prise de rendez-vous et de visite.
                </Typography>
                <Grid container spacing={1.5}>
                  {weeklyAvailability.map((slot, index) => (
                    <Grid item xs={12} key={slot.dayOfWeek}>
                      {(() => {
                        const slotInvalid = hasInvalidTimeRange(slot);
                        return (
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography sx={{ minWidth: 90 }}>{dayLabels[index]}</Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={slot.isActive}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setWeeklyAvailability((prev) => prev.map((row) => (
                                  row.dayOfWeek === slot.dayOfWeek ? { ...row, isActive: checked } : row
                                )));
                              }}
                            />
                          }
                          label={slot.isActive ? 'Actif' : 'Inactif'}
                        />
                        <TextField
                          type="time"
                          size="small"
                          label="Début"
                          value={slot.startTime}
                          disabled={!slot.isActive}
                          error={slotInvalid}
                          onChange={(e) => {
                            const value = e.target.value;
                            setWeeklyAvailability((prev) => prev.map((row) => (
                              row.dayOfWeek === slot.dayOfWeek ? { ...row, startTime: value } : row
                            )));
                          }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ max: slot.endTime || undefined }}
                        />
                        <TextField
                          type="time"
                          size="small"
                          label="Fin"
                          value={slot.endTime}
                          disabled={!slot.isActive}
                          error={slotInvalid}
                          helperText={slotInvalid ? "Fin doit être > Début" : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setWeeklyAvailability((prev) => prev.map((row) => (
                              row.dayOfWeek === slot.dayOfWeek ? { ...row, endTime: value } : row
                            )));
                          }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ min: slot.startTime || undefined }}
                        />
                      </Box>
                        );
                      })()}
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      color={availabilityValidationError ? 'error' : 'text.secondary'}
                    >
                      {availabilityValidationError || (availabilityLoading ? 'Enregistrement automatique...' : 'Les changements sont enregistrés automatiquement.')}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Sécurité
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ mb: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentUser.otpEnabled}
                      onChange={handleToggleOtp}
                      disabled={otpLoading}
                    />
                  }
                  label="Activer l'authentification à deux facteurs (2FA)"
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte.
                  Lorsqu'elle est activée, vous devrez saisir un code envoyé à votre adresse e-mail à chaque connexion.
                </Typography>
              </Box>
              
              <Typography variant="subtitle1" gutterBottom>
                Changer le mot de passe
              </Typography>
              
              <Formik
                initialValues={{
                  oldPassword: '',
                  newPassword: '',
                  confirmPassword: ''
                }}
                validationSchema={passwordSchema}
                onSubmit={handleChangePassword}
              >
                {({ errors, touched, handleChange, handleBlur }) => (
                  <Form>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="oldPassword"
                          label="Mot de passe actuel"
                          type="password"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.oldPassword && Boolean(errors.oldPassword)}
                          helperText={touched.oldPassword && errors.oldPassword}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="newPassword"
                          label="Nouveau mot de passe"
                          type="password"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.newPassword && Boolean(errors.newPassword)}
                          helperText={touched.newPassword && errors.newPassword}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="confirmPassword"
                          label="Confirmer le mot de passe"
                          type="password"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                          helperText={touched.confirmPassword && errors.confirmPassword}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={passwordLoading}
                        >
                          {passwordLoading ? <CircularProgress size={24} /> : 'Changer le mot de passe'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                )}
              </Formik>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default Profile; 
